// Single source of truth for turning a URL query string into marketing
// attribution fields.
//
// Why this exists: ad platforms are inconsistent. Meta ad URL templates get
// pasted with capitalised keys, alternative names (adset_name, ad_id,
// campaign_name), or only a subset of the utm_* set. Google/TikTok/Bing send
// their own click ids. Previously we matched five exact lowercase keys, so any
// deviation meant the lead was stored with no ad attribution at all.
//
// This module is browser-safe (no window/document access) so it can run in the
// browser, during SSR, and inside the worker request handler.

export type TrackingFields = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  /** Raw platform click ids (fbclid, gclid, ttclid, etc.). */
  fbclid: string | null;
  gclid: string | null;
  /** Ad platform object IDs passed through the URL. */
  utm_campaign_id: string | null;
  utm_adset_id: string | null;
  utm_ad_id: string | null;
};

/** Canonical field -> accepted query keys, in priority order. */
// ID fields are listed FIRST so they win over name fields when a key can
// mean either (e.g. campaign_id is the numeric ID, not the campaign name).
const ALIASES: Record<keyof TrackingFields, string[]> = {
  utm_campaign_id: ["campaign_id", "campaignid", "campaign_ID"],
  utm_adset_id: ["adset_id", "adsetid", "ad_set_id", "fbc_id", "fbadsetid"],
  utm_ad_id: ["ad_id", "adid", "ad_ID", "h_ad_id", "fbadid"],
  utm_source: ["utm_source", "source", "src", "utmsource"],
  utm_medium: ["utm_medium", "medium", "utmmedium", "adset_name", "adsetname", "ad_set_name", "adset", "ad_set"],
  utm_campaign: [
    "utm_campaign", "campaign", "campaign_name", "campaignname",
    "utmcampaign", "utm_campaign_name", "utm_id",
  ],
  utm_content: [
    "utm_content", "content", "ad_name", "adname", "ad", "creative_name",
    "creative", "utm_ad", "utmcontent",
  ],
  utm_term: ["utm_term", "term", "utmterm"],
  fbclid: ["fbclid", "fb_click_id"],
  gclid: ["gclid", "wbraid", "gbraid", "gclsrc_id"],
};

/** Click ids that identify a paid platform even with no utm_source present. */
const CLICK_ID_SOURCES: Array<{ keys: string[]; source: string; medium: string }> = [
  { keys: ["fbclid"], source: "facebook", medium: "paid" },
  { keys: ["gclid", "wbraid", "gbraid"], source: "google", medium: "cpc" },
  { keys: ["ttclid"], source: "tiktok", medium: "paid" },
  { keys: ["msclkid"], source: "bing", medium: "cpc" },
  { keys: ["li_fat_id"], source: "linkedin", medium: "paid" },
  { keys: ["twclid"], source: "twitter", medium: "paid" },
  { keys: ["epik"], source: "pinterest", medium: "paid" },
  { keys: ["sccid", "ScCid"], source: "snapchat", medium: "paid" },
];

/** Normalise common source typos/capitalisation so reports are consistent. */
function normaliseSource(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim().toLowerCase();
  if (s === "facebook" || s === "fb" || s === "meta") return "facebook";
  if (s === "instagram" || s === "ig") return "instagram";
  if (s === "google" || s === "gads" || s === "adwords" || s === "youtube") return "google";
  if (s === "tiktok" || s === "tt") return "tiktok";
  if (s === "bing" || s === "microsoft") return "bing";
  if (s === "linkedin" || s === "li") return "linkedin";
  return v.trim();
}

function normaliseMedium(v: string | null): string | null {
  if (!v) return null;
  const s = v.trim().toLowerCase();
  if (s === "ppc" || s === "cpc") return "cpc";
  if (s === "paid" || s === "paid_social" || s === "paidsocial" || s === "social_paid") return "paid";
  return v.trim();
}

/** Any of these appearing in a URL means "this is a fresh campaign click". */
export function hasTrackingParams(search: string | URLSearchParams): boolean {
  const map = lowerMap(search);
  if (map.size === 0) return false;
  for (const keys of Object.values(ALIASES)) {
    if (keys.some((k) => map.has(k.toLowerCase()))) return true;
  }
  for (const { keys } of CLICK_ID_SOURCES) {
    if (keys.some((k) => map.has(k.toLowerCase()))) return true;
  }
  return false;
}

function lowerMap(search: string | URLSearchParams): Map<string, string> {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
      : search;
  const map = new Map<string, string>();
  params.forEach((value, key) => {
    const k = key.trim().toLowerCase();
    const v = (value ?? "").trim();
    // Meta leaves unresolved templates like "{{adset.name}}" when the ad is
    // misconfigured - treat those as absent instead of storing junk.
    if (!v || v.includes("{{") || v === "--" || v.toLowerCase() === "null" || v.toLowerCase() === "undefined") return;
    if (!map.has(k)) map.set(k, v);
  });
  return map;
}

function clip(v: string | null | undefined, n: number): string | null {
  if (!v) return null;
  const s = v.trim();
  return s ? s.slice(0, n) : null;
}

const LIMITS: Record<keyof TrackingFields, number> = {
  utm_source: 190, utm_medium: 190, utm_campaign: 290,
  utm_content: 290, utm_term: 190, fbclid: 490, gclid: 490,
  utm_campaign_id: 120, utm_adset_id: 120, utm_ad_id: 120,
};

const ID_FIELDS: Array<keyof TrackingFields> = [
  "utm_campaign_id", "utm_adset_id", "utm_ad_id",
];


/**
 * Parse tracking fields out of a query string, case-insensitively, with alias
 * and click-id fallbacks. Returns only the fields actually present.
 */
export function parseTrackingParams(search: string | URLSearchParams): Partial<TrackingFields> {
  const map = lowerMap(search);
  const out: Partial<TrackingFields> = {};
  if (map.size === 0) return out;

  for (const field of Object.keys(ALIASES) as Array<keyof TrackingFields>) {
    for (const alias of ALIASES[field]) {
      const v = map.get(alias.toLowerCase());
      if (v) {
        out[field] = clip(v, LIMITS[field]);
        break;
      }
    }
  }

  // Normalise source/medium values so reports stay clean even when the ad
  // template sends "Facebook" / "Fb" / "Paid_Social" / "PPC".
  if (out.utm_source) out.utm_source = normaliseSource(out.utm_source);
  if (out.utm_medium) out.utm_medium = normaliseMedium(out.utm_medium);

  // A bare click id (Meta auto-appends fbclid even when URL params are unset)
  // still tells us the platform - never leave such a lead unattributed.
  if (!out.utm_source) {
    for (const { keys, source, medium } of CLICK_ID_SOURCES) {
      if (keys.some((k) => map.has(k.toLowerCase()))) {
        out.utm_source = source;
        if (!out.utm_medium) out.utm_medium = medium;
        break;
      }
    }
  }

  return out;
}

/** Referrer-derived platform, used only when the URL carried nothing at all. */
export function inferSourceFromReferrer(referrer: string | null): { utm_source: string; utm_medium: string } | null {
  if (!referrer) return null;
  let host = "";
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (/(^|\.)facebook\.com$|(^|\.)fb\.(com|me)$/.test(host)) return { utm_source: "facebook", utm_medium: "referral" };
  if (/(^|\.)instagram\.com$/.test(host)) return { utm_source: "instagram", utm_medium: "referral" };
  if (/(^|\.)google\./.test(host)) return { utm_source: "google", utm_medium: "organic" };
  if (/(^|\.)tiktok\.com$/.test(host)) return { utm_source: "tiktok", utm_medium: "referral" };
  if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(host)) return { utm_source: "youtube", utm_medium: "referral" };
  if (/(^|\.)bing\.com$/.test(host)) return { utm_source: "bing", utm_medium: "organic" };
  return null;
}

/** Cookie name written server-side by the worker on the landing request. */
export const SERVER_ATTR_COOKIE = "lead_attr_srv";
